import type { TrackPoint } from "./offline-draft";

export type NativeRecorderTrackPoint = TrackPoint & {
  nativePointId: number;
  activeMilliseconds: number;
};

export type NativeRecoveryCursor = {
  afterPointId?: number;
  afterTimestamp?: number;
};

export type NativeTrackingSnapshot = {
  active: boolean;
  activityId?: string;
  state?: "recording" | "paused" | "finished";
  startedAt?: number;
  elapsedMilliseconds?: number;
  lastPointId?: number;
  updatedAt?: number;
};

export function nativePointToTrackPoint(point: {
  nativePointId: number;
  latitude: number;
  longitude: number;
  altitude?: number | null;
  accuracy: number;
  timestamp: number;
  activeMilliseconds: number;
}): NativeRecorderTrackPoint {
  return {
    nativePointId: point.nativePointId,
    latitude: point.latitude,
    longitude: point.longitude,
    altitude: point.altitude ?? null,
    accuracy: point.accuracy,
    timestamp: point.timestamp,
    activeMilliseconds: Math.max(0, point.activeMilliseconds),
    activeSeconds: Math.floor(Math.max(0, point.activeMilliseconds) / 1000),
  };
}

export function selectUnseenNativePoints(
  points: NativeRecorderTrackPoint[],
  cursor: NativeRecoveryCursor,
): NativeRecorderTrackPoint[] {
  const byId = new Map<number, NativeRecorderTrackPoint>();
  for (const point of points) {
    if (!Number.isFinite(point.nativePointId) || point.nativePointId <= 0) continue;
    if (cursor.afterPointId && point.nativePointId <= cursor.afterPointId) continue;
    if (!cursor.afterPointId && cursor.afterTimestamp && point.timestamp <= cursor.afterTimestamp) continue;
    byId.set(point.nativePointId, point);
  }
  return [...byId.values()].sort((a, b) =>
    a.nativePointId - b.nativePointId || a.timestamp - b.timestamp,
  );
}

export function advanceNativeCursor(
  cursor: NativeRecoveryCursor,
  points: NativeRecorderTrackPoint[],
): NativeRecoveryCursor {
  if (!points.length) return { ...cursor };
  const last = points.reduce((highest, point) =>
    point.nativePointId > highest.nativePointId ? point : highest,
  );
  return {
    afterPointId: last.nativePointId,
    afterTimestamp: Math.max(cursor.afterTimestamp ?? 0, ...points.map((point) => point.timestamp)),
  };
}

export function cursorFromTrack(points: Array<TrackPoint & { nativePointId?: number }>): NativeRecoveryCursor {
  const last = points.at(-1);
  if (!last) return {};
  const nativePointId = points.reduce(
    (max, point) => Math.max(max, point.nativePointId ?? 0),
    0,
  );
  return {
    afterPointId: nativePointId > 0 ? nativePointId : undefined,
    afterTimestamp: last.timestamp > 0 ? last.timestamp : undefined,
  };
}
